import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import type {FilterObject} from 'nutshell-core/types';

import {PageControlBar} from 'shells/control-bar/page-control-bar';
// @ts-expect-error - imported from .js/.jsx file
import {AudienceIcon} from 'shells/icon/svg/audience-icon';
import {MessageIcon} from 'shells/icon/svg/message-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {LoadingPage} from 'shells/loading-page/loading-page';

import {getEditionStatusBarInfo} from '../../../editions/edition-helpers';
import {EditionRecipientList} from '../../../editions/recipient-list/edition-recipient-list';

import {appHistory} from '../../../../../history';
import {useGetSessionUser} from '../../../../core/use-get-session-user';
import {PageContainer, PageTitleBar} from '../../../../layout';
import {
    generateUpdatedSearch,
    updateFilterValues,
} from '../../../../reports/generate-url-updating-param';
import type {ReportURLParams} from '../../../../reports/report-types';

import {SMS_EDITION_FILTER_KEYS} from '../../consts';
import {editionFieldFilterFunction} from '../../recipient-list/helpers';
import {SmsEditionRecipientEngagementFilter} from '../../recipient-list/sms-edition-recipient-engagement-filter';

import {
    useGetSmsDripEdition,
    useGetSmsDripSequenceEditions,
    useUpdateSmsDripEdition,
    useCreateSmsDripSequenceEdition,
} from '../graphql/hooks';

import {DripSequenceMiniMapSidebar} from './drip-sequence-mini-map-sidebar';
import {EditionDetails} from './edition-details';

type Props = ContextRouter & {
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
};

export function DripSequenceEditionPage(props: Props) {
    const activeTab: string = props.match.params.tab || 'details';

    const {user} = useGetSessionUser();

    const {createDripEdition} = useCreateSmsDripSequenceEdition();
    const {smsDripEdition, isLoading} = useGetSmsDripEdition(
        props.match.params.dripSequenceEditionId
    );
    const {
        editions: dripEditions,
        isLoading: isLoadingEditionsList,
        error,
    } = useGetSmsDripSequenceEditions({
        dripSequenceId: props.match.params.dripSequenceId,
        limit: 12,
    });

    function onCreateDripEdition() {
        if (!smsDripEdition) return;

        createDripEdition({campaignId: smsDripEdition.campaign.id}).then((result) => {
            if (result && result.data && result.data.smsDripEditionCreate) {
                appHistory.push(result.data.smsDripEditionCreate.edition.htmlUrlPath);
            }
        });
    }

    const {updateEdition} = useUpdateSmsDripEdition();

    const isDraft = smsDripEdition?.status === 'NEW';
    const title = smsDripEdition ? smsDripEdition.name : 'Drip edition';

    const onEditionChange = (newEditionId: string) => {
        // update urlParams filter keys with newEditionId
        const newFilter = updateFilterValues(
            props.urlParams.filter,
            // @ts-expect-error
            SMS_EDITION_FILTER_KEYS,
            newEditionId
        );

        const getNewSearch = generateUpdatedSearch(props.urlParams);

        // get new search string using the updated filter
        const newSearch = getNewSearch(
            {
                filter: newFilter,
            },
            true
        ); // These filters need to be merged

        // update the path with the newEditionId
        const newPath = generatePath(props.match.path, {
            dripSequenceId: props.match.params.dripSequenceId,
            dripSequenceEditionId: newEditionId,
            tab: activeTab,
        });

        // navigate to the new path with new search string
        appHistory.replace({pathname: newPath, search: newSearch});
    };

    const tabOptions = [
        {
            id: 'details',
            label: 'Details',
            icon: MessageIcon,
            href: generatePath(props.match.path, {
                dripSequenceId: props.match.params.dripSequenceId,
                dripSequenceEditionId: props.match.params.dripSequenceEditionId,
                tab: 'details',
            }),
            component: smsDripEdition ? (
                <EditionDetails
                    edition={smsDripEdition}
                    onUpdate={updateEdition}
                    key={smsDripEdition.id}
                />
            ) : (
                <LoadingPage />
            ),
        },
    ];

    if (!isDraft && smsDripEdition) {
        tabOptions.push({
            id: 'recipients',
            label: 'Recipients',
            icon: AudienceIcon,
            href: generatePath(props.match.path, {
                dripSequenceId: props.match.params.dripSequenceId,
                dripSequenceEditionId: props.match.params.dripSequenceEditionId,
                tab: 'recipients',
            }),
            component: (
                <EditionRecipientList
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    urlParams={props.urlParams}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    edition={smsDripEdition}
                    campaignType='DRIP_SEQUENCE'
                    graduatedFilters={
                        <SmsEditionRecipientEngagementFilter
                            editionId={smsDripEdition.id}
                            filters={props.activeFilters}
                            onParamChange={props.onParamChange}
                            urlParams={props.urlParams}
                        />
                    }
                    fieldFilterFunction={editionFieldFilterFunction}
                />
            ),
        });
    }

    const isOwner = Boolean(
        user &&
            smsDripEdition &&
            smsDripEdition.campaign &&
            smsDripEdition.campaign.owner &&
            smsDripEdition.campaign.owner.id === user.id
    );

    return (
        <div className='flex full-height full-width pos-absolute'>
            {smsDripEdition && (
                <DripSequenceMiniMapSidebar
                    selectedEditionId={smsDripEdition.id}
                    editions={dripEditions}
                    error={error}
                    isLoading={isLoadingEditionsList}
                    onEditionChange={(editionId: string) => {
                        onEditionChange(editionId);
                    }}
                    onUpdateEdition={({editionId, delay}) => {
                        return updateEdition({editionId: editionId, sendDelay: delay});
                    }}
                    canAddNewEdition={isDraft}
                    onCreateEdition={onCreateDripEdition}
                />
            )}
            <div className='flex-grow pos-relative'>
                <PageContainer
                    title={title}
                    pageTitleBar={
                        <PageTitleBar
                            title={title}
                            subheaderPrimaryText='Edit your edition'
                            noMaxWidth={true}
                            breadcrumbs={[
                                {
                                    to: '/email-marketing/sms/drip-sequences',
                                    text: 'Drip sequences',
                                },
                                {
                                    to: smsDripEdition ? smsDripEdition.campaign.htmlUrlPath : '#',
                                    text: smsDripEdition
                                        ? smsDripEdition.campaign.name
                                        : 'Campaign',
                                },
                            ]}
                            editInPlaceProps={{
                                onChange: (newValue: string | null | undefined) => {
                                    return smsDripEdition
                                        ? updateEdition({
                                              editionId: smsDripEdition.id,
                                              name: newValue,
                                          })
                                        : undefined;
                                },
                                placeholder: 'Name this edition',
                            }}
                        />
                    }
                    controlBar={
                        <PageControlBar
                            statusInfo={
                                smsDripEdition
                                    ? getEditionStatusBarInfo(
                                          smsDripEdition,
                                          Boolean(user && user.permissions.canAccessFullCampaigns),
                                          undefined,
                                          isLoading,
                                          isOwner
                                      )
                                    : undefined
                            }
                            tabs={{
                                selectedTab: activeTab,
                                options: tabOptions,
                            }}
                        />
                    }
                >
                    {/* @ts-expect-error */}
                    <TabbedContent selectedTab={activeTab} tabOptions={tabOptions} />
                </PageContainer>
            </div>
        </div>
    );
}
